import React, { useState } from 'react';
import { Send, MessageSquare, Mail, Building, Sparkles, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { contactInfo } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';
import axios from 'axios';

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-gradient-to-b from-white to-grey/10 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Background */}
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-mid-purple/5 rounded-full blur-hero" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Side - Content */}
          <AnimateOnScroll animation="fade-right">
            <div>
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-mid-purple/20 to-mid-pink/20 text-mid-purple px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-purple/20">
                <Sparkles size={12} />
                {t('contact.badge')}
              </span>
              
              <h2 className="font-display text-4xl md:text-5xl font-bold text-black dark:text-white mb-6">
                {t('contact.title')}
                <br />
                <span className="gradient-text">{t('contact.titleHighlight')}</span>
              </h2>
              
              <p className="text-dark-grey dark:text-gray-400 text-lg leading-relaxed mb-10">
                {t('contact.description')}
              </p>

              {/* Contact Info */}
              <div className="space-y-4">
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-2xl border border-grey/20 dark:border-gray-700 hover:border-mid-purple/30 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-mid-purple/10 flex items-center justify-center group-hover:bg-mid-purple/20 transition-colors">
                    <Mail size={20} className="text-mid-purple" />
                  </div>
                  <div>
                    <p className="text-sm text-dark-grey dark:text-gray-500">{t('contact.email')}</p>
                    <p className="font-medium text-black dark:text-white">{contactInfo.email}</p>
                  </div>
                </a>

                <a
                  href={`https://wa.me/${contactInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-2xl border border-grey/20 dark:border-gray-700 hover:border-mid-green/30 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-mid-green/10 flex items-center justify-center group-hover:bg-mid-green/20 transition-colors">
                    <Phone size={20} className="text-mid-green" />
                  </div>
                  <div>
                    <p className="text-sm text-dark-grey dark:text-gray-500">{t('contact.whatsapp')}</p>
                    <p className="font-medium text-black dark:text-white">{contactInfo.phone}</p>
                  </div>
                </a>
              </div>
            </div>
          </AnimateOnScroll>

          {/* Right Side - Form */}
          <AnimateOnScroll animation="fade-left" delay={0.2}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-grey/10 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-black dark:text-white mb-6 flex items-center gap-2">
                <MessageSquare size={20} className="text-mid-purple" />
                {t('contact.formTitle')}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-grey dark:text-gray-400 mb-2">{t('contact.name')} *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.namePlaceholder')}
                      required
                      className="rounded-xl border-grey/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-mid-purple focus:ring-mid-purple/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-grey dark:text-gray-400 mb-2">{t('contact.email')} *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.emailPlaceholder')}
                      required
                      className="rounded-xl border-grey/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-mid-purple focus:ring-mid-purple/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-grey dark:text-gray-400 mb-2">
                    <Building size={14} className="inline mr-1" />
                    {t('contact.company')}
                  </label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.companyPlaceholder')}
                    className="rounded-xl border-grey/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-mid-purple focus:ring-mid-purple/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-grey dark:text-gray-400 mb-2">{t('contact.message')} *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.messagePlaceholder')}
                    rows={5}
                    required
                    className="rounded-xl border-grey/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-mid-purple focus:ring-mid-purple/20 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-gray-100 text-white dark:text-black rounded-xl py-6 font-mono uppercase tracking-wider btn-lift shadow-lg"
                >
                  {isSubmitting ? (
                    t('contact.sending')
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      {t('contact.send')}
                    </>
                  )}
                </Button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-mid-green/10 border border-mid-green/20 rounded-xl text-mid-green text-sm text-center">
                    {t('contact.success')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm text-center">
                    {t('contact.error')}
                  </div>
                )}
              </form>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
